import { cx } from '@emotion/css';
import { Button, IconButton, MotifScheme } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { IntoPageFixedPortal } from '../../../../components/Page/PageFixedPortal';
import type { ContentfulIdVariable } from '../../../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../../../hooks/useContentfulQuery';
import { renderRichText } from '../../../../utils/renderText/renderRichText';
import { AgentMessage } from './AgentMessage/AgentMessage';
import { aiAgentQuery } from './AiAgent.query';
import {
  bottomSafeCss,
  conversationAreaCss,
  disclaimerContainerCss,
  fabButtonCss,
  minimizeButtonCss,
  panelBodyCss,
  panelContainerCss,
  panelHeaderCss,
  suggestionChipCss,
  suggestionsContainerCss,
  welcomeContainerCss,
  welcomeTitleCss,
} from './AiAgent.styles';
import { ChatInput } from './ChatInput/ChatInput';
import { useAiAgent } from './hooks/useAiAgent';
import { LoadingIndicator } from './LoadingIndicator/LoadingIndicator';
import type { AiAgentDataHandlerProps } from './types';
import { UserMessage } from './UserMessage/UserMessage';

export const AiAgent: FC<{ id: string }> = props => {
  const { data } = useContentfulQuery<AiAgentDataHandlerProps, ContentfulIdVariable>(aiAgentQuery, {
    variables: { id: props.id },
  });

  const {
    isOpen,
    setIsOpen,
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isStreaming,
    sendMessage,
    resendLastMessage,
  } = useAiAgent();

  const conversationRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (behavior: 'smooth' | 'auto' = 'smooth') => {
    requestAnimationFrame(() => {
      conversationRef.current?.scrollTo({
        top: conversationRef.current.scrollHeight,
        behavior,
      });
    });
  };

  // Scroll to bottom of conversation when any new messages are added
  useEffect(() => {
    if (messages.length) {
      scrollToBottom('smooth');
    }
  }, [messages]);

  // Scroll to bottom of conversation instantly when the panel is opened
  useEffect(() => {
    if (isOpen) {
      scrollToBottom('auto');
    }
  }, [isOpen, data?.aiAgent]);

  if (!data?.aiAgent) return null;

  const { placeholderText, suggestedPrompts, disclaimer } = data.aiAgent;

  const suggestions = suggestedPrompts?.filter(Boolean) ?? [];
  const hasMessages = messages.length > 0;

  if (!isOpen) {
    return (
      <IntoPageFixedPortal>
        <IconButton
          className={fabButtonCss}
          iconName="ai-agent"
          aria-label="Open AI assistant"
          onClick={() => setIsOpen(true)}
        />
      </IntoPageFixedPortal>
    );
  }

  return (
    <IntoPageFixedPortal>
      <section className={cx('creators-ai-agent', MotifScheme.DEFAULT, panelContainerCss)}>
        <div className={panelHeaderCss}>
          <IconButton
            className={minimizeButtonCss}
            iconName="minimize-arrows"
            aria-label="Minimize"
            onClick={() => setIsOpen(false)}
          />
        </div>
        <div className={panelBodyCss}>
          {!hasMessages && (
            <div className={welcomeContainerCss}>
              <h2 className={welcomeTitleCss}>{data.aiAgent.welcomeMessage}</h2>
            </div>
          )}
          <div className={conversationAreaCss} ref={conversationRef}>
            {suggestions.length > 0 && (
              <div className={suggestionsContainerCss}>
                {suggestions.map(suggestion => (
                  <Button
                    key={suggestion}
                    className={suggestionChipCss}
                    onClick={() => sendMessage(suggestion!)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            )}
            {messages.map((message, index) => {
              if (message.role === 'user') {
                return <UserMessage key={message.id} content={message.content} />;
              }

              // Only mark as latest when the agent's response is the final message,
              // i.e., the user hasn't sent a follow-up yet
              // This will restrict the redo button from being shown too early
              const isLatest = index === messages.length - 1;

              return (
                <AgentMessage
                  key={message.id}
                  message={message}
                  isLatest={isLatest}
                  onRedo={resendLastMessage}
                  onToggleSources={() => isLatest && scrollToBottom('smooth')}
                />
              );
            })}
            {isLoading && <LoadingIndicator />}
          </div>
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={sendMessage}
            placeholder={placeholderText ?? undefined}
            isDisabled={isLoading || isStreaming}
          />
          {disclaimer && <div className={disclaimerContainerCss}>{renderRichText(disclaimer)}</div>}
          <div className={bottomSafeCss} />
        </div>
      </section>
    </IntoPageFixedPortal>
  );
};

AiAgent.displayName = 'AiAgent';
